// Mini dialog opened from WordActionPopover when the user wants to start a
// new speaker at a specific word position. Lets them either pick one of the
// existing speakers (most common case: the AI lumped two people into one
// SPEAKER_X) or create a brand-new speaker — with either a manual name or
// the auto-generated SPEAKER_N suggestion.

import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

// Find the smallest free integer N such that SPEAKER_N isn't already used.
// Handles both numeric speaker IDs and renamed labels — only the
// `SPEAKER_(\d+)` shape contributes to the "used numbers" set.
function nextFreeSpeakerId(existing) {
  const used = new Set();
  for (const name of existing || []) {
    const match = /^SPEAKER_(\d+)$/.exec(String(name || ''));
    if (match) used.add(Number(match[1]));
  }
  let i = 0;
  while (used.has(i)) i += 1;
  return `SPEAKER_${i}`;
}

export default function SpeakerSplitDialog({
  open,
  onClose,
  existingSpeakers,
  getSpeakerColor,
  onConfirm,
}) {
  // Tracks which existing-speaker chip is selected. null means: not chosen
  // from the chip list. Custom names go through `newName` instead.
  const [selectedChip, setSelectedChip] = React.useState(null);
  // When the user wants a brand-new speaker, we render an editable text
  // field. Empty string = field not active yet; non-empty = "new speaker"
  // mode is open and `newName` is the live value.
  const [newName, setNewName] = React.useState('');
  const [newOpen, setNewOpen] = React.useState(false);
  const newInputRef = React.useRef(null);

  // Reset everything when the dialog opens — never carry state across splits.
  React.useEffect(() => {
    if (open) {
      setSelectedChip(null);
      setNewName('');
      setNewOpen(false);
    }
  }, [open]);

  const handlePickChip = (speaker) => {
    setSelectedChip(speaker);
    setNewOpen(false);
    setNewName('');
  };

  const handleOpenNewSpeaker = () => {
    setSelectedChip(null);
    const suggestion = nextFreeSpeakerId(existingSpeakers);
    setNewName(suggestion);
    setNewOpen(true);
    // Focus + select-all so the user can type a custom name immediately
    // or accept the suggestion with Enter.
    setTimeout(() => {
      const el = newInputRef.current;
      if (el) {
        el.focus();
        el.select?.();
      }
    }, 0);
  };

  const handleUseSuggestion = () => {
    setNewName(nextFreeSpeakerId(existingSpeakers));
  };

  const trimmedNew = newName.trim();
  const newNameCollision =
    newOpen
    && trimmedNew.length > 0
    && (existingSpeakers || []).includes(trimmedNew);
  const effectiveSpeaker = newOpen ? trimmedNew : selectedChip;
  const canConfirm = Boolean(effectiveSpeaker) && !newNameCollision;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm?.(effectiveSpeaker);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Wer spricht ab hier?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Wähle einen bestehenden Sprecher oder lege einen neuen an.
          Das Segment wird ab dem angeklickten Wort getrennt.
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          sx={{ flexWrap: 'wrap', rowGap: 1, mb: 2 }}
        >
          {(existingSpeakers || []).map((speaker) => {
            const isSelected = selectedChip === speaker;
            return (
              <Chip
                key={speaker}
                label={speaker}
                onClick={() => handlePickChip(speaker)}
                variant={isSelected ? 'filled' : 'outlined'}
                color={isSelected ? 'primary' : 'default'}
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: getSpeakerColor?.(speaker) || '#666',
                      ml: '8px !important',
                    }}
                  />
                }
                sx={{ fontSize: '0.8rem' }}
              />
            );
          })}
        </Stack>

        {!newOpen ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddAlt1OutlinedIcon />}
            onClick={handleOpenNewSpeaker}
          >
            Neuer Sprecher
          </Button>
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                inputRef={newInputRef}
                size="small"
                fullWidth
                label="Name des neuen Sprechers"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canConfirm) {
                    e.preventDefault();
                    handleConfirm();
                  }
                }}
                error={newNameCollision}
                helperText={
                  newNameCollision
                    ? 'Dieser Name ist bereits einem bestehenden Sprecher zugewiesen.'
                    : 'Beliebiger Name (z.B. „Anna" oder „Moderator") oder klick auf das Sparkle-Icon für eine automatische SPEAKER_N-ID.'
                }
              />
              <Tooltip title="Automatisch nächste freie SPEAKER_N nehmen">
                <span>
                  <Button
                    size="small"
                    onClick={handleUseSuggestion}
                    sx={{ minWidth: 0, px: 1 }}
                  >
                    <AutoAwesomeOutlinedIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
        >
          Trennen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
